import axios from 'axios'
import { retryWithBackoff, debounce } from '@/utils/rateLimit'

const state = {
  transactions: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  filters: {
    type: '',
    category: '',
    startDate: '',
    endDate: '',
    paymentMethod: '',
    sortBy: 'date',
    sortOrder: 'desc'
  },
  loading: false,
  error: null,
  // Add cache for API responses
  cache: {
    transactions: { data: null, timestamp: null, ttl: 2 * 60 * 1000 } // 2 minutes
  }
}

const mutations = {
  SET_TRANSACTIONS(state, transactions) {
    state.transactions = transactions
    // Cache the data
    state.cache.transactions = {
      data: { transactions, pagination: state.pagination },
      timestamp: Date.now(),
      ttl: state.cache.transactions.ttl
    }
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = pagination
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  ADD_TRANSACTION(state, transaction) {
    state.transactions.unshift(transaction)
    // Clear cache when data changes
    state.cache.transactions = { data: null, timestamp: null, ttl: state.cache.transactions.ttl }
  },
  UPDATE_TRANSACTION(state, updatedTransaction) {
    const index = state.transactions.findIndex(t => t.id === updatedTransaction.id)
    if (index !== -1) {
      state.transactions.splice(index, 1, updatedTransaction)
      // Clear cache when data changes
      state.cache.transactions = { data: null, timestamp: null, ttl: state.cache.transactions.ttl }
    }
  },
  REMOVE_TRANSACTION(state, transactionId) {
    state.transactions = state.transactions.filter(t => t.id !== transactionId)
    // Clear cache when data changes
    state.cache.transactions = { data: null, timestamp: null, ttl: state.cache.transactions.ttl }
  },
  CLEAR_CACHE(state) {
    Object.keys(state.cache).forEach(key => {
      state.cache[key] = { data: null, timestamp: null, ttl: state.cache[key].ttl }
    })
  }
}

const actions = {
  async fetchTransactions({ commit, state }, params = {}) {
    // Check cache first
    const cache = state.cache.transactions
    if (cache.data && cache.timestamp && (Date.now() - cache.timestamp < cache.ttl) && !params.page && !params.limit) {
      console.log('📋 Using cached transactions data')
      commit('SET_TRANSACTIONS', cache.data.transactions)
      commit('SET_PAGINATION', cache.data.pagination)
      return cache.data
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const queryParams = { ...state.filters, ...params }
      const response = await retryWithBackoff(async () => {
        return await axios.get('/transactions', { params: queryParams })
      })
      
      commit('SET_TRANSACTIONS', response.data.transactions)
      commit('SET_PAGINATION', response.data.pagination)
      return response.data
    } catch (error) {
      console.error('Fetch transactions error:', error)
      
      // Handle authentication errors
      if (error.response?.status === 401) {
        console.log('🚫 Authentication error in fetchTransactions - no auth required')
        throw error
      }
      
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch transactions')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Debounced version for frequent calls
  async fetchTransactionsDebounced({ dispatch }, params = {}) {
    return await debounce('transactions', () => 
      dispatch('fetchTransactions', params), 300
    )
  },

  async createTransaction({ commit }, transactionData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
              const response = await axios.post('/transactions', transactionData)
      commit('ADD_TRANSACTION', response.data)
      return response.data
    } catch (error) {
      console.error('Create transaction error:', error)
      commit('SET_ERROR', error.response?.data?.error || 'Failed to create transaction')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateTransaction({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
              const response = await axios.put(`/transactions/${id}`, data)
      commit('UPDATE_TRANSACTION', response.data)
      return response.data
    } catch (error) {
      console.error('Update transaction error:', error)
      commit('SET_ERROR', error.response?.data?.error || 'Failed to update transaction')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteTransaction({ commit }, transactionId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
              await axios.delete(`/transactions/${transactionId}`)
      commit('REMOVE_TRANSACTION', transactionId)
    } catch (error) {
      console.error('Delete transaction error:', error)
      commit('SET_ERROR', error.response?.data?.error || 'Failed to delete transaction')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getTransaction({ commit }, transactionId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
              const response = await axios.get(`/transactions/${transactionId}`)
      return response.data
    } catch (error) {
      console.error('Get transaction error:', error)
      commit('SET_ERROR', error.response?.data?.error || 'Failed to get transaction')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setFilters({ commit }, filters) {
    commit('SET_FILTERS', filters)
  },

  clearFilters({ commit }) {
    commit('SET_FILTERS', {
      type: '',
      category: '',
      startDate: '',
      endDate: '',
      paymentMethod: '',
      sortBy: 'date',
      sortOrder: 'desc'
    })
  }
}

const getters = {
  transactions: state => state.transactions,
  pagination: state => state.pagination,
  filters: state => state.filters,
  loading: state => state.loading,
  error: state => state.error,
  hasTransactions: state => state.transactions.length > 0,
  totalTransactions: state => state.pagination.total
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 